import React, { useState, useEffect } from 'react';

import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { Card, Table, Input, Button, Tag, Select, Row, Col, Modal, Form, message } from 'antd';

import SuperAdminAPI from '../../services/SuperAdminAPI';

const { Option } = Select;

const statusOptions = ['paid', 'unpaid', 'overdue', 'disputed'];
const paymentMethods = ['credit_card', 'bank_transfer', 'paypal', 'manual'];

const BillingManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();

  useEffect(() => {
    fetchInvoices();
    fetchTenants();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = (await SuperAdminAPI.listInvoices?.()) || [];
      setInvoices(data);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const data = await SuperAdminAPI.listTenants();
      setTenants(data || []);
    } catch {
      setTenants([]);
    }
  };

  const handleSearch = e => setSearch(e.target.value);
  const handleStatus = value => setStatus(value);
  const handleMethod = value => setMethod(value);

  const filtered = invoices.filter(
    i =>
      (!search ||
        (i.tenantName || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.invoiceId || '').toLowerCase().includes(search.toLowerCase())) &&
      (!status || i.status === status) &&
      (!method || i.paymentMethod === method)
  );

  const columns = [
    { title: 'Tenant', dataIndex: 'tenantName' },
    { title: 'Invoice #', dataIndex: 'invoiceId' },
    { title: 'Date', dataIndex: 'date', render: d => (d ? new Date(d).toLocaleDateString() : '—') },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (a, record) => `${a} ${record.currency || 'KWD'}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: s => (
        <Tag
          color={
            s === 'paid' ? 'green' : s === 'overdue' ? 'red' : s === 'disputed' ? 'orange' : 'blue'
          }
        >
          {s}
        </Tag>
      ),
    },
    { title: 'Payment Method', dataIndex: 'paymentMethod', render: m => <Tag>{m}</Tag> },
  ];

  const handleAddInvoice = async () => {
    try {
      const values = await addForm.validateFields();
      await SuperAdminAPI.createInvoice(values);
      message.success('Invoice created successfully!');
      setAddModalOpen(false);
      fetchInvoices();
    } catch {
      message.error('Failed to create invoice.');
    }
  };

  return (
    <Card
      title="Billing Management"
      extra={
        <Button icon={<PlusOutlined />} type="primary" onClick={() => setAddModalOpen(true)}>
          Generate Invoice
        </Button>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Input
            placeholder="Search invoices"
            prefix={<SearchOutlined />}
            value={search}
            onChange={handleSearch}
            allowClear
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Select
            placeholder="Status"
            value={status}
            onChange={handleStatus}
            allowClear
            style={{ width: '100%' }}
          >
            {statusOptions.map(s => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Select
            placeholder="Payment Method"
            value={method}
            onChange={handleMethod}
            allowClear
            style={{ width: '100%' }}
          >
            {paymentMethods.map(m => (
              <Option key={m} value={m}>
                {m}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={filtered}
        rowKey="invoiceId"
        loading={loading}
        pagination={{ pageSize: 10 }}
        bordered
        size="middle"
      />
      <Modal
        title="Generate Invoice"
        open={addModalOpen}
        onOk={handleAddInvoice}
        onCancel={() => setAddModalOpen(false)}
        okText="Create"
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="Tenant"
            name="tenantId"
            rules={[{ required: true, message: 'Please select tenant' }]}
          >
            <Select placeholder="Select tenant">
              {tenants.map(t => (
                <Option key={t._id} value={t._id}>
                  {t.name || t.slug || t._id}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          >
            <Input type="number" placeholder="Enter amount" />
          </Form.Item>
          <Form.Item label="Currency" name="currency">
            <Input placeholder="KWD" />
          </Form.Item>
          <Form.Item
            label="Payment Method"
            name="paymentMethod"
            rules={[{ required: true, message: 'Please select payment method' }]}
          >
            <Select placeholder="Select payment method">
              {paymentMethods.map(m => (
                <Option key={m} value={m}>
                  {m}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              {statusOptions.map(s => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default BillingManagement;
